import { ComponentRef, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { GasIcon } from '../../utils/gas-icons/icon/gas-icon.namespace';
import { GasHeaderMeta } from './gas-header.interface';
import { GasBreadcrumb } from '../../common/gas-breadcrumbs/gas-breadcrumbs.interface';
import { skip } from 'rxjs/operators';

const DEFAULT_TITLE = 'Нет заголовка';

/**
 * @description Сервис для управления шапкой сайта
 * @example this.gasHeaderService.setMeta({ title: 'my title', icon: GasIcon.Layout.Success });
 * @example this.gasHeaderService.title('Title');
 * @example this.gasHeaderService.title = 'title';
 */
@Injectable({
  providedIn: 'root',
})
export class GasHeaderService<T = any> {
  public title$: Observable<string>;
  public subtitle$: Observable<string>;
  public icon$: Observable<GasIcon.Icons | string>;
  public iconColor$: Observable<string>;
  public iconClass$: Observable<string>;
  public breadcrumbs$: Observable<GasBreadcrumb[]>;
  public component$: Observable<T>;

  public componentRef$: Observable<ComponentRef<T>>;

  /** Устанавливаем значение заголовка */
  public set title(text: string) {
    this.setTitle(text);
  }

  /** Получаем значение заголовка */
  public get title(): string {
    return this.titleSubject$.getValue();
  }

  /** Устанавливаем значение подзаголовка */
  public set subtitle(text: string) {
    this.setSubtitle(text);
  }

  /** Получаем значение подзаголовка */
  public get subtitle(): string {
    return this.subtitleSubject$.getValue();
  }

  /** Устанавливаем иконку */
  public set icon(icon: GasIcon.Icons | string) {
    this.setIcon(icon);
  }

  /** Получаем иконку */
  public get icon(): GasIcon.Icons | string {
    return this.iconSubject$.getValue();
  }

  /** Устанавливаем иконку */
  public set iconColor(color: string) {
    this.iconColorSubject$.next(color);
  }

  /** Получаем иконку */
  public get iconColor(): string {
    return this.iconColorSubject$.getValue();
  }

  /** Устанавливаем иконку */
  public set iconClass(className: string) {
    this.iconClassSubject$.next(className);
  }

  /** Получаем иконку */
  public get iconClass(): string {
    return this.iconClassSubject$.getValue();
  }

  /** Устанавливаем хлебные крошки */
  public set breadcrumbs(breadcrumbs: GasBreadcrumb[]) {
    this.setBreadcrumbs(breadcrumbs);
  }

  /** Получаем хлебные крошки */
  public get breadcrumbs(): GasBreadcrumb[] {
    return this.breadcrumbsSubject$.getValue();
  }

  private titleSubject$ = new BehaviorSubject<string>(DEFAULT_TITLE);
  private subtitleSubject$ = new BehaviorSubject<string>('');
  private iconSubject$ = new BehaviorSubject<GasIcon.Icons | string>('');
  private iconColorSubject$ = new BehaviorSubject<string>(null);
  private iconClassSubject$ = new BehaviorSubject<string>(null);
  private breadcrumbsSubject$ = new BehaviorSubject<GasBreadcrumb[]>([]);
  private componentSubject$ = new Subject<any>();

  private componentRefSubject$ = new BehaviorSubject<ComponentRef<T>>(undefined);

  constructor() {
    this.title$ = this.titleSubject$.asObservable();
    this.subtitle$ = this.subtitleSubject$.asObservable();
    this.icon$ = this.iconSubject$.asObservable();
    this.iconColor$ = this.iconColorSubject$.asObservable();
    this.iconClass$ = this.iconClassSubject$.asObservable();
    this.breadcrumbs$ = this.breadcrumbsSubject$.asObservable();
    this.component$ = this.componentSubject$.asObservable();
    this.componentRef$ = this.componentRefSubject$.asObservable().pipe(skip(1));
  }

  /** Установка заголовка шапки, передаем title */
  public setTitle(title: string): void {
    this.titleSubject$.next(title || DEFAULT_TITLE);
  }

  /** Установка подзаголовка, если не установили, то ничего не отобразиться */
  public setSubtitle(subtitle: string): void {
    this.subtitleSubject$.next(subtitle);
  }

  /** Установка иконки, принимает любую существующую иконку и устанавливает размер 40 */
  public setIcon(icon: GasIcon.Icons | string): void {
    this.iconSubject$.next(icon);
  }

  /** Установка хлебных крошек */
  public setBreadcrumbs(breadcrumbs: GasBreadcrumb[]): void {
    this.breadcrumbsSubject$.next(breadcrumbs);
  }

  public createComponent(container: any): ComponentRef<T> {
    this.componentSubject$.next(container);
    return this.componentRefSubject$.getValue();
  }

  public setComponentRef(componentRef: ComponentRef<T>): void {
    this.componentRefSubject$.next(componentRef);
  }

  /** Заполняет всеми необходимыми данными шапку */
  public setMeta(meta: GasHeaderMeta): void {
    this.titleSubject$.next(meta.title);

    if (meta.subtitle) {
      this.subtitleSubject$.next(meta.subtitle);
    }

    if (meta.icon) {
      this.iconSubject$.next(meta.icon);
    }
  }
}
